import React, { useContext, useState } from 'react'
import useId from '@mui/material/utils/useId'
import { IconButton } from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  QuestionContextMenuContext,
  MARK_FOR_REVIEW_STATUS_ID,
} from '@app/components/common/QuestionContextMenuContext'
import { faEllipsisH } from '@fortawesome/pro-solid-svg-icons/faEllipsisH'
import { faPennant } from '@fortawesome/pro-solid-svg-icons/faPennant'
import { faHighlighterLine } from '@fortawesome/pro-solid-svg-icons/faHighlighterLine'
import { faArrowLeftRotate } from '@fortawesome/pro-solid-svg-icons/faArrowLeftRotate'
import { DropdownMenu, DropdownMenuOptions } from '@app/components/ui/DropdownMenu'
import { useSnapshot } from 'valtio'
import { CurrentTestState } from '@app/storage'

type Props = {
  hasHighlights?: boolean
  hideMarkForReview?: boolean
  markedItems?: string[]
  checkHighlightedElement: () => boolean
  onMarkForReview?: () => void
  onStickyNotes?: () => void
  onHighlightSelection?: () => void
  onStrikethrough?: () => void
  onResetHighlight?: () => void
  onRemoveHighlight: () => void
}

const QuestionToolsMenu: React.FC<Props> = ({
  hasHighlights = false,
  hideMarkForReview = false,
  markedItems,
  onMarkForReview,
  // onStickyNotes,
  onHighlightSelection,
  // onStrikethrough,
  onResetHighlight,
  checkHighlightedElement,
  onRemoveHighlight,
}) => {
  const id = useId()
  const { pendingStatusAnnouncementId } = useContext(QuestionContextMenuContext) ?? {}

  const {
    currentItemResponse: { itemId },
  } = useSnapshot(CurrentTestState)

  const [selectedText, setSelectedText] = useState('')
  const [isHighlightedElement, setIsHighlightedElement] = useState(false)

  const onMenuOpen = () => {
    const selection = window.getSelection().toString()
    if (!selection) {
      selectedText && setSelectedText('')
      return
    }

    setIsHighlightedElement(checkHighlightedElement)
    setSelectedText(selection)
  }

  const options: DropdownMenuOptions[] = [
    ...(hideMarkForReview
      ? []
      : [
          {
            label: `${markedItems.includes(itemId) ? 'Unmark' : 'Mark'} for Review`,
            icon: <FontAwesomeIcon icon={faPennant} />,
            action: onMarkForReview,
          },
        ]),
    // {
    //   label: "Sticky Notes",
    //   icon: <FontAwesomeIcon icon={faNoteSticky} />,
    //   action: onStickyNotes
    // },
    ...(selectedText
      ? [
          {
            label: `${isHighlightedElement ? 'Unhighlight' : 'Highlight'} Selection`,
            icon: <FontAwesomeIcon icon={faHighlighterLine} />,
            action: isHighlightedElement ? onRemoveHighlight : onHighlightSelection,
          },
        ]
      : []),
    // {
    //   label: "Strikethrough",
    //   icon: <FontAwesomeIcon icon={faStrikethrough} />,
    //   action: onStrikethrough
    // }
  ]

  if (hasHighlights) {
    options.push({
      label: 'Reset Highlight',
      icon: <FontAwesomeIcon icon={faArrowLeftRotate} />,
      action: onResetHighlight,
    })
  }

  // Mark for Review is the only unconditional option, so without it the menu can
  // be empty — and an empty Menu still opens on click.
  if (!options.length) return null

  return (
    <DropdownMenu
      id={id}
      onClick={onMenuOpen}
      ariaHasPopup={false}
      anchor={
        <IconButton
          size='small'
          aria-label='More options'
          {...(pendingStatusAnnouncementId === MARK_FOR_REVIEW_STATUS_ID
            ? { 'aria-describedby': MARK_FOR_REVIEW_STATUS_ID }
            : {})}
        >
          <FontAwesomeIcon icon={faEllipsisH} />
        </IconButton>
      }
      options={options}
    />
  )
}

export default QuestionToolsMenu
